import React, { useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useGetBetHistoryQuery, useLazyExportBetHistoryQuery } from '@/store/api/betReportApi';
import { debounce } from '@/utils';
import Partners from '@/components/common/Partners';
import DatePickerInput from '@/components/common/DatePickerInput';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import PageSizeSelector from '@/components/common/PageSizeSelector';
import { showToastMessage } from '@/helpers';

function UnsettledBet() {
  const { t } = useTranslation();
  const categoryMenu = [
    { label: t('reports.unsettledBet.sportAll'), value: 'All' },
    { label: t('reports.unsettledBet.sportCricket'), value: 'Cricket' },
    { label: t('reports.unsettledBet.sportFootball'), value: 'Soccer' },
    { label: t('reports.unsettledBet.sportTennis'), value: 'Tennis' },
    { label: t('reports.unsettledBet.sportGreyhound'), value: 'Greyhound' },
    { label: t('reports.unsettledBet.sportHorseRacing'), value: 'HorseRacing' },
  ];
  const [sport, setSport] = useState('Sports');
  const [fromDate, setFromDate] = useState(dayjs().subtract(15, 'day').format('DD/MM/YYYY HH:mm'));
  const [toDate, setToDate] = useState(dayjs().format('DD/MM/YYYY HH:mm'));
  const [appliedFrom, setAppliedFrom] = useState(
    dayjs().subtract(15, 'day').startOf('day').toISOString()
  );
  const [appliedTo, setAppliedTo] = useState(dayjs().endOf('day').toISOString());
  const [appliedSport, setAppliedSport] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useGetBetHistoryQuery({
    page,
    limit,
    betTime: 'CURRENT',
    fromDate: appliedFrom,
    toDate: appliedTo,
    ...(appliedSport && appliedSport !== 'All' && { sport: appliedSport }),
    search: searchTerm,
  });

  const bets = data?.bets ?? [];
  const pagination = data?.pagination;
  const totalItems = pagination?.totalItems ?? 0;

  const [triggerExport, { isFetching: isExporting }] = useLazyExportBetHistoryQuery();

  const parseDate = (str: string) => {
    const [datePart = '', timePart = '00:00'] = str.split(' ');
    const [dd, mm, yyyy] = datePart.split('/');
    return dayjs(`${yyyy}-${mm}-${dd}T${timePart}`);
  };

  const handleGo = () => {
    setAppliedFrom(parseDate(fromDate).toISOString());
    setAppliedTo(parseDate(toDate).toISOString());
    setAppliedSport(sport === 'Sports' ? '' : sport);
    setPage(1);
  };

  const handleReset = () => {
    const defaultFrom = dayjs().subtract(15, 'day');
    const defaultTo = dayjs();
    setFromDate(defaultFrom.format('DD/MM/YYYY HH:mm'));
    setToDate(defaultTo.format('DD/MM/YYYY HH:mm'));
    setAppliedFrom(defaultFrom.startOf('day').toISOString());
    setAppliedTo(defaultTo.endOf('day').toISOString());
    setSport('Sports');
    setAppliedSport('');
    setSearchTerm('');
    setPage(1);
  };

  const debouncedSearch = useRef(
    debounce((val: string) => {
      setSearchTerm(val);
      setPage(1);
    }, 500)
  ).current;

  const handleExport = async (exportFormat: 'Csv' | 'Pdf') => {
    try {
      await triggerExport({
        betTime: 'CURRENT',
        fromDate: appliedFrom,
        toDate: appliedTo,
        ...(appliedSport && appliedSport !== 'All' && { sport: appliedSport }),
        search: searchTerm,
        exportFormat,
        fileName: `unsettled-bet-${dayjs().format('YYYYMMDD-HHmmss')}`,
      }).unwrap();

      showToastMessage('success', t('reports.unsettledBet.exportStartedToast'));
    } catch {
      showToastMessage('error', t('reports.unsettledBet.exportFailedToast'));
    }
  };

  return (
    <div className="mt-1 lg:mt-[var(--top-offset)] md:mx-2 mb-[15px] md:mb-6 font-sans text-sm text-black dark:bg-darkPanelBg bg-white  pb-10  shadow-sm rounded-b-[6px]">
      <PageHeading title={t('reports.unsettledBet.title')} className="text-black dark:text-white" />

      {/* Filter row */}
      <div className="bg-white dark:bg-darkPanelBg py-2 grid grid-cols-1 sm:grid-cols-2 md:flex md:items-center gap-2 md:gap-5 w-full px-0.5 md:px-0">
        <select
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          className="border border-[#fec733] rounded px-2 py-1 text-[12px] bg-white w-full md:max-w-[255px] min-w-[130px] h-[30px] dark:border-[#808080] dark:bg-transparent dark:text-white"
        >
          {categoryMenu.map((opt) => (
            <option className="dark:text-black" key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <DatePickerInput
          value={fromDate}
          onChange={setFromDate}
          wrapperClassName="!w-full md:!max-w-[255px] block"
        />

        <DatePickerInput
          value={toDate}
          onChange={setToDate}
          wrapperClassName="!w-full md:!max-w-[255px] block"
        />

        <div className="flex gap-1.5">
          <button
            onClick={handleGo}
            style={{
              background: 'linear-gradient(180deg, #fec733, #253926)',
              border: '1px solid #fec733',
            }}
            className="text-white text-[12px] h-[30px] px-5 rounded-[5px] leading-[15px]"
          >
            {t('reports.unsettledBet.go')}
          </button>

          <button
            onClick={handleReset}
            style={{
              background: 'linear-gradient(180deg, #fec733, #253926)',
              border: '1px solid #fec733',
            }}
            className="text-white text-[12px] h-[30px] px-5 rounded-[5px] leading-[15px]"
          >
            {t('reports.unsettledBet.reset')}
          </button>
        </div>
      </div>

      {/* Search + export + entries row */}
      <div className="bg-white dark:bg-darkPanelBg py-2 flex items-center gap-1 md:gap-2">
        <input
          type="text"
          placeholder={t('reports.unsettledBet.searchPlaceholder')}
          onChange={(e) => debouncedSearch(e.target.value)}
          className="border border-[#fec733] dark:border-[#808080] dark:text-white dark:bg-darkPanelBg rounded px-2 py-1 text-[12px] w-40 h-[30px]"
        />

        <button
          title={t('reports.unsettledBet.exportCsv')}
          className="hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handleExport('Csv')}
          disabled={isExporting}
        >
          <img src="/images/csv-icon.svg" alt="CSV" className="w-6 h-6 md:w-7 md:h-7" />
        </button>

        <button
          title={t('reports.unsettledBet.exportPdf')}
          className="hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handleExport('Pdf')}
          disabled={isExporting}
        >
          <img src="/images/pdf-icon.svg" alt="PDF" className="w-6 h-6 md:w-7 md:h-7" />
        </button>

        <div className="ml-auto">
          <PageSizeSelector
            pageSize={limit}
            pageSizeOptions={[25, 50, 100]}
            onPageSizeChange={(size) => {
              setLimit(size);
              setPage(1);
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-t-0 border-gray-300">
        <table className="w-full text-[12px] border-collapse max-md:min-w-[900px]">
          <thead>
            <tr
              style={{ background: 'linear-gradient(#fec733, #fbac40)' }}
              className="text-black dark:text-white"
            >
              {[
                t('reports.unsettledBet.columns.date'),
                t('reports.unsettledBet.columns.competition'),
                t('reports.unsettledBet.columns.event'),
                t('reports.unsettledBet.columns.market'),
                t('reports.unsettledBet.columns.runner'),
                t('reports.unsettledBet.columns.side'),
                t('reports.unsettledBet.columns.line'),
                t('reports.unsettledBet.columns.rate'),
                t('reports.unsettledBet.columns.amount'),
                t('reports.unsettledBet.columns.liability'),
                t('reports.unsettledBet.columns.potentialProfit'),
              ].map((col, i) => (
                <th
                  key={col}
                  className={`text-left p-[6px] leading-3 border border-[#e09518] font-medium whitespace-nowrap ${
                    i === 2 ? 'min-w-[200px]' : 'min-w-[120px]'
                  }`}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-black ">
            {isLoading || isFetching ? (
              Array.from({ length: limit }).map((_, i) => (
                <tr key={i} className="bg-white dark:bg-transparent">
                  {Array.from({ length: 11 }).map((__, j) => (
                    <td
                      key={j}
                      className="p-[6px] leading-3.5 border border-gray-200 min-w-[120px]"
                    >
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4" />
                    </td>
                  ))}
                </tr>
              ))
            ) : bets.length === 0 ? (
              <tr>
                <td
                  colSpan={11}
                  className="text-center py-1 font-medium text-black dark:text-white"
                >
                  {t('reports.unsettledBet.noBets')}
                </td>
              </tr>
            ) : (
              (bets as any[]).map((bet, i) => {
                const side = (bet.betOn ?? '').toLowerCase();
                const rowBg = side === 'back' ? 'bg-[#8EC5FE]' : 'bg-[#FFA0D0]';
                const liability = bet.liability != null ? Number(bet.liability).toFixed(2) : '-';
                const potentialProfit =
                  bet.potentialProfit != null ? Number(bet.potentialProfit).toFixed(2) : '-';

                return (
                  <tr key={bet.id ?? i} className={`font-medium text-[12px] ${rowBg}`}>
                    <td className="p-[6px] leading-3 border border-gray-200 whitespace-nowrap min-w-[120px]">
                      {bet.placedAt ? dayjs(bet.placedAt).format('D/M/YYYY, h:mm:ss a') : '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200   min-w-[120px]">
                      {bet.competition || bet.event?.competition || bet.sport || '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 w-full min-w-[250px]">
                      {bet.event?.name ?? '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 w-full min-w-[120px]">
                      {bet.marketName ?? '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 min-w-[120px]">
                      {bet.selection ?? '-'}
                    </td>
                    <td
                      className={`p-[6px] leading-3 border border-gray-200 font-medium min-w-[120px] ${
                        side === 'back' ? 'text-blue-600' : 'text-pink-600'
                      }`}
                    >
                      {side === 'back'
                        ? t('reports.unsettledBet.back')
                        : side === 'lay'
                          ? t('reports.unsettledBet.lay')
                          : side || '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 text-center min-w-[120px]">
                      {bet.line ?? '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 text-center min-w-[120px]">
                      {bet.odds ?? '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 text-right min-w-[120px]">
                      {bet.amount != null ? Number(bet.amount).toFixed(2) : '-'}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 text-right min-w-[120px] text-red-600 font-semibold">
                      {liability}
                    </td>
                    <td className="p-[6px] leading-3 border border-gray-200 text-right min-w-[120px] text-green-700 font-semibold">
                      {potentialProfit}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        totalItems={totalItems}
        currentPage={page}
        pageSize={limit}
        pageSizeOptions={[10, 25, 50, 100]}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setLimit(size);
          setPage(1);
        }}
        showEntriesInfo
      />
    </div>
  );
}

export default UnsettledBet;
